<template>
  <div class="queue-number-container">
    <!-- Priority Notice Banner -->
    <div v-if="isPriority" class="priority-notice mb-4">
      <i class="fas fa-star me-2"></i> Priority Transaction
    </div>

    <div class="queue-ticket">
      <div class="queue-header">
        <h2 class="institution-name" style="color: #fff !important">Hercor College</h2>
        <h3 class="queue-title" style="color: #fff !important">Express Transaction Queue</h3>
      </div>
      
      <div class="queue-body">
        <div class="queue-info">
          <div class="queue-type">
            {{ selectedTransactionType?.display_name || 'General Transaction' }}
          </div>
          
          <div class="number-container">
            <div class="number-label">Your Queue Number</div>
            <div class="number" :class="{'priority-number': isPriority}">
              {{ formatQueueNumber }}
            </div>
          </div>
          
          <div v-if="student" class="student-info">
            <div class="student-name">{{ student.lastname }}, {{ student.firstname }} {{ student.middlename ? student.middlename : '' }}</div>
            <div class="student-id">ID: {{ student.student_id_number }}</div>
          </div>
          <div v-else-if="transaction && transaction.guest_name" class="student-info">
            <div class="student-name">{{ transaction.guest_name }}</div>
          </div>

          <div class="transaction-amount">Amount: ₱{{ amount }}</div>
          
          <div class="timestamp">
            {{ currentDateTime }}
          </div>
        </div>
      </div>
      
      <div class="queue-footer">
        <p>Please wait for your number to be called.</p>
        <p>Thank you for your patience!</p>
      </div>
    </div>
    
    <div class="print-notice" v-if="isPrinting">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p>Printing ticket, please wait...</p>
    </div>

    <div class="print-status" v-if="printMessage && !isPrinting">
      <p :class="printMessageType === 'success' ? 'text-success' : (printMessageType === 'warning' ? 'text-warning' : 'text-danger')">
        {{ printMessage }}
      </p>
    </div>
    
    <div class="queue-actions" v-if="!isPrinting">
      <div class="row">
        <div class="col-6">
          <button 
            class="btn btn-primary btn-lg btn-block print-btn" 
            @click="handlePrintTicket"
            :disabled="isPrinting || loading"
          >
            <i class="fas fa-print"></i> Print Again
          </button>
        </div>
        <div class="col-6">
          <button 
            class="btn btn-success btn-lg btn-block done-btn" 
            @click="finishAndReset"
          >
            <i class="fas fa-check"></i> Done <span class="countdown">({{ countdown }}s)</span>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Hidden iframe for actual printing -->
    <iframe 
      ref="printFrame" 
      :srcdoc="printContent" 
      style="position: absolute; width: 0; height: 0; border: 0;"
    ></iframe>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
//import { EscPos } from '@/utils/escpos';
import { kioskTransactionsApi } from '@/services/kioskApi';

export default {
  name: 'QueueNumber',
  props: {
    config: {
      type: Object,
      default: () => ({
        printTicket: true,
        showEstimatedTime: true
      })
    },
    transactionData: {
      type: Object,
      default: () => ({})
    },
    transactionId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      isPrinting: false,
      currentDateTime: '',
      localTransactionId: null,
      printContent: '',
      countdown: 15,
      countdownInterval: null,
      transaction: null,
      kioskId: localStorage.getItem('kioskId') || '',
      printerName: '',
      printMessage: '',
      printMessageType: ''
    };
  },
  computed: {
    ...mapGetters({
      queueNumber: 'kiosk/getQueueNumber',
      student: 'kiosk/getStudent',
      selectedTransactionType: 'kiosk/getSelectedTransactionType',
      isPriority: 'kiosk/isPriorityMode',
      amount: 'kiosk/getTransactionAmount',
      loading: 'kiosk/isLoading',
      error: 'kiosk/getError'
    }),
    formatQueueNumber() {
      // Only use the queue_info_number from the transaction
      if (this.transaction && this.transaction.queue_info_number) {
        //console.log('Using queue_info_number from transaction:', this.transaction.queue_info_number);
        return this.transaction.queue_info_number;
      }
      
      // Fallback only if transaction is not available yet
      return '--';
    },
    // Compute the effective transaction ID from either props or route params
    effectiveTransactionId() {
      return this.localTransactionId || this.transactionId;
    }
  },
  async created() {
    console.group('QueueNumber Component Initialization');
    
    // Determine printer name based on kiosk ID
    this.updatePrinterName();
    console.log('Using printer:', this.printerName);
    
    // Get transaction ID from props or transaction data
    if (this.transactionId) {
      this.localTransactionId = this.transactionId;
    } else if (this.transactionData && this.transactionData.transactionId) {
      this.localTransactionId = this.transactionData.transactionId;
    }
    
    // Safety net: if no transaction was created earlier in the flow, create one now.
    if (!this.effectiveTransactionId) {
      try {
        const created = await this.$store.dispatch('kiosk/createTransaction');
        if (created && created.id) {
          this.localTransactionId = created.id.toString();
        }
      } catch (error) {
        console.error('Failed to create transaction as fallback:', error);
      }
    }
    
    // IMPORTANT: Fetch transaction details as early as possible
    // to ensure we have the queue_info_number for printing
    if (this.effectiveTransactionId) {
      try {
        await this.fetchTransactionDetails();
      } catch (error) {
        console.error('Failed to fetch transaction details:', error);
      }
    }
    
    // Format current date and time
    const now = new Date();
    this.currentDateTime = now.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    // Auto print the ticket immediately when component is mounted, AFTER fetching transaction details
    // Use a very short delay to allow component to fully render
    this.$nextTick(() => {
      setTimeout(() => {
        if (this.config.printTicket !== false) {
          this.handlePrintTicket();
        }
      }, 500);
    });
    
    // Start countdown timer
    this.startCountdown();
    
    console.groupEnd();
  },
  mounted() {
    // In case the auto-print doesn't trigger the countdown, we start it here too
    if (!this.countdownInterval) {
      this.startCountdown();
    }
  },
  beforeUnmount() {
    this.clearCountdown();
  },
  methods: {
    ...mapActions({
      resetKiosk: 'kiosk/resetKiosk'
    }),
    
    // Add method to fetch transaction details
    async fetchTransactionDetails() {
      if (!this.effectiveTransactionId) {
        console.warn('Cannot fetch transaction: No transaction ID available');
        return Promise.reject(new Error('No transaction ID'));
      }
      
      //console.log('Fetching transaction details for ID:', this.effectiveTransactionId);
      
      try {
        const response = await kioskTransactionsApi.getTransaction(this.effectiveTransactionId);
        this.transaction = response.data;
        //console.log('Transaction details fetched:', this.transaction);
        return this.transaction;
      } catch (error) {
        console.error('Error fetching transaction details:', error);
        throw error; // Re-throw to be properly caught by callers
      }
    },
    
    startCountdown() {
      // Clear any existing interval to avoid duplicates
      this.clearCountdown();
      
      // Start the countdown
      this.countdownInterval = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          this.finishAndReset();
        }
      }, 1000);
    },
    
    clearCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },
    
    async handlePrintTicket() {
      if (this.isPrinting) {
        console.warn('Print operation canceled: Already printing');
        return;
      }

      if (!this.effectiveTransactionId) {
        console.warn('Print operation canceled: No transaction ID available');
        return;
      }

      console.group('Print Ticket Process');
      try {
        this.isPrinting = true;
        this.printMessage = '';
        console.log('Starting print process for transaction ID:', this.effectiveTransactionId);
        
        // Generate print content first (in case we need the browser fallback)
        this.generatePrintContent();
        
        // Primary: local printer agent (same origin - the kiosk page is served
        // by the same local PHP server that talks to the thermal printer).
        // Printing never leaves the kiosk machine.
        try {
          console.log('Attempting to print with the local printer agent...');
          const result = await this.printWithEscPos();
          console.log('Local print agent result:', result);
          
          if (result && result.success) {
            console.log('Printing via local agent successful');
            this.printMessage = 'Ticket printed';
            this.printMessageType = 'success';
            this.isPrinting = false;
            console.groupEnd();
            return;
          }
          throw new Error(result && result.error ? result.error : 'Local print agent returned failure');
        } catch (agentError) {
          console.error('Local printer agent failed:', agentError);
          
          // Last resort: browser printing if the agent is unreachable,
          // instead of the old backend (web) print path which can never work.
          console.log('Falling back to browser printing...');
          this.printMessage = 'Local printer offline — using browser print.';
          this.printMessageType = 'warning';
          
          this.$nextTick(() => {
            if (this.$refs.printFrame) {
              try {
                this.$refs.printFrame.contentWindow.print();
              } catch (printError) {
                console.error('Browser printing error:', printError);
              }
            } else {
              console.warn('Print frame not found for browser printing');
            }
          });
          
          setTimeout(() => {
            this.isPrinting = false;
          }, 2000);
        }
        
      } catch (error) {
        console.error('Print process failed with error:', error);
        if (error.stack) {
          console.error('Error stack:', error.stack);
        }
        this.printMessage = 'Printing failed. Tap Print Again or call staff.';
        this.printMessageType = 'danger';
        this.isPrinting = false;
      } finally {
        console.groupEnd();
      }
    },
    async printWithEscPos() {
      console.group('Local Printer Agent Printing');
      try {
        const queueNumberToDisplay = this.transaction?.queue_info_number || '--';
        console.log('Sending to local printer agent...');
        
        // Same-origin: the kiosk page is served by the same local PHP server
        // that owns the thermal printer, so no CORS / private-network issues.
        const kioskId = localStorage.getItem('kioskId') || '';
        const printUrl = `${window.location.origin}/print?printer=${encodeURIComponent(this.printerName)}&kiosk=${encodeURIComponent(kioskId)}`;
        
        console.log(`Using local print URL: ${printUrl}`);
        
        // Prepare print data
        const printData = {
          queueNumber: queueNumberToDisplay,
          student: this.student ? {
            name: `${this.student.lastname}, ${this.student.firstname} ${this.student.middlename || ''}`,
            id: this.student.student_id_number
          } : null,
          transactionType: this.selectedTransactionType?.display_name || 'General Transaction',
          isPriority: this.isPriority,
          timestamp: new Date().toISOString(),
          amount: this.amount
        };

        // Send to the local printer agent
        const response = await fetch(printUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(printData)
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Local print agent error response:', errorText);
          throw new Error(`Local print agent error: ${response.status} - ${errorText}`);
        }

        const result = await response.json();
        console.log('Print result:', result);
        return result;
      } catch (error) {
        console.error('Local printer agent error:', error);
        throw error;
      } finally {
        console.groupEnd();
      }
    },
    generatePrintContent() {
      //console.log('Generating HTML print content');
      
      // Always use the queue_info_number from the database 
      const queueInfoNumber = this.transaction?.queue_info_number || '--';
      //console.log('Will display queue number in HTML:', queueInfoNumber);
      
      // Create HTML content for browser printing
      this.printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Queue Ticket</title>
          <style>
            @page {
              size: 80mm 200mm;
              margin: 0;
            }
            body {
              font-family: Arial, sans-serif;
              width: 80mm;
              margin: 0;
              padding: 5mm;
              text-align: center;
            }
            .header {
              font-size: 14pt;
              font-weight: bold;
              margin-bottom: 5mm;
            }
            .sub-header {
              font-size: 12pt;
              margin-bottom: 5mm;
            }
            .queue-number {
              font-size: 24pt;
              font-weight: bold;
              padding: 5mm;
              border: 1px solid #000;
              margin: 5mm 0;
              border-radius: 3mm;
            }
            .priority {
              background-color: #fff3cd;
            }
            .footer {
              margin-top: 10mm;
              font-size: 10pt;
              border-top: 1px dashed #000;
              padding-top: 3mm;
            }
            .info {
              margin: 3mm 0;
            }
          </style>
        </head>
        <body>
          <div class="header">Hercor College</div>
          <div class="sub-header">Express Transaction Queue</div>
          
          ${this.student ? `
          <div class="info">
            ${this.student.lastname}, ${this.student.firstname} ${this.student.middlename ? this.student.middlename : ''}
          </div>
          <div class="info">
            ID: ${this.student.student_id_number}
          </div>
          ` : ''}
          
          <div class="info">Your Queue Number</div>
          <div class="queue-number ${this.isPriority ? 'priority' : ''}">
            ${queueInfoNumber}
          </div>
          
          <div class="info">
            ${this.selectedTransactionType?.display_name || 'General Transaction'}
          </div>
          
          <div class="info">
            Status: ${this.isPriority ? 'Priority' : 'Regular'}
          </div>

          <div class="info">
            Amount: ₱${this.amount}
          </div>
          
          <div class="info">
            ${this.currentDateTime}
          </div>
          
          <div class="footer">
            <p>Please wait for your number to be called.</p>
            <p>Thank you for your patience!</p>
          </div>
        </body>
        </html>
      `;
      //console.log('HTML content generated successfully');
      //console.log('Queue Info Number from transaction:', this.transaction?.queue_info_number);
      //console.log('Formatted Queue Number:', this.formatQueueNumber);
      //console.log('Is Priority:', this.isPriority);
    },
    finishAndReset() {
      // Clear the countdown timer
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
      
      // Reset the kiosk state
      this.resetKiosk();
      
      // Emit step-complete event with a special flag to go back to welcome
      this.$emit('step-complete', {
        completed: true,
        transactionId: this.effectiveTransactionId,
        goToWelcome: true
      });
    },
    updatePrinterName() {
      const kioskId = localStorage.getItem('kioskId');
      
      if (kioskId === 'kiosk-1') {
        this.printerName = 'XP-58'; // port 9000
      } else if (kioskId === 'kiosk-2') {
        this.printerName = 'XP-58';  // port 9002
      } else {
        this.printerName = 'USB001'; // Default to RONGTA on USB001
      }
      
      console.log(`Printer set to "${this.printerName}" based on kiosk ID: ${kioskId || 'not set'}`);
    }
  }
};
</script>

<style scoped>
.queue-number-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 1rem;
}

.queue-ticket {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 500px;
  margin-bottom: 2rem;
  overflow: hidden;
}

.queue-header {
  background: linear-gradient(135deg, var(--brand-dark) 0%, var(--brand-primary) 120%);
  color: white;
  text-align: center;
  padding: 1.5rem;
  border-bottom: 5px solid var(--brand-accent);
}

.institution-name {
  font-size: 1.8rem;
  margin: 0;
}

.queue-title {
  font-size: 1.2rem;
  margin: 0.5rem 0 0;
  opacity: 0.9;
}

.queue-body {
  padding: 2rem;
  text-align: center;
}

.queue-type {
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--brand-primary);
  margin-bottom: 1.5rem;
}

.number-container {
  margin: 1.5rem 0;
}

.number-label {
  font-size: 1.2rem;
  color: #555;
  margin-bottom: 0.5rem;
}

.number {
  font-family: var(--font-heading);
  font-size: 4rem;
  font-weight: bold;
  color: var(--brand-primary);
  padding: 0.5rem 1rem;
  border: 3px solid var(--brand-primary);
  border-radius: 10px;
  display: inline-block;
  min-width: 200px;
}

.priority-number {
  color: var(--brand-dark);
  border-color: var(--brand-accent);
  background-color: var(--brand-accent);
}

.student-info {
  margin: 1.5rem 0;
}

.student-name {
  font-size: 1.2rem;
  font-weight: bold;
}

.student-id {
  font-size: 1rem;
  color: #555;
}

.timestamp {
  font-size: 0.9rem;
  color: #777;
  margin-top: 1rem;
}

.queue-footer {
  background-color: var(--brand-surface);
  border-top: 1px dashed var(--brand-border);
  padding: 1rem;
  text-align: center;
  font-size: 0.9rem;
  color: var(--secondary-text);
}

.queue-footer p {
  margin: 0.25rem 0;
}

.print-notice {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1.5rem;
}

.queue-actions {
  width: 100%;
  max-width: 500px;
}

.btn-block {
  width: 100%;
}

.print-btn, .done-btn {
  padding: 1rem;
  font-size: 1.2rem;
  border-radius: 12px;
}

.print-btn {
  background-color: var(--brand-primary);
  border-color: var(--brand-primary);
}

.print-btn:hover {
  background-color: var(--brand-cyan);
  border-color: var(--brand-cyan);
}

.done-btn {
  background-color: var(--brand-accent);
  border-color: var(--brand-accent);
  color: var(--brand-dark);
}

.done-btn:hover {
  background-color: #ffd45e;
  border-color: #ffd45e;
  color: var(--brand-dark);
}

.countdown {
  font-size: 1.2rem;
}

.priority-notice {
  background-color: var(--brand-accent);
  color: var(--brand-dark);
  padding: 1rem;
  border-radius: 8px;
  width: 100%;
  max-width: 800px;
  text-align: center;
  font-size: 1.3rem;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(255, 200, 34, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
}

/* Portrait mode adjustments */
@media screen and (orientation: portrait) {
  .queue-ticket {
    max-width: 90%;
  }
  
  .institution-name {
    font-size: 2.2rem;
  }
  
  .queue-title {
    font-size: 1.5rem;
  }
  
  .queue-type {
    font-size: 1.8rem;
  }
  
  .number-label {
    font-size: 1.5rem;
  }
  
  .number {
    font-size: 5rem;
    min-width: 240px;
  }
  
  .student-name {
    font-size: 1.5rem;
  }
  
  .student-id {
    font-size: 1.2rem;
  }
  
  .print-btn, .done-btn {
    padding: 1.5rem;
    font-size: 1.6rem;
  }
  
  .countdown {
    font-size: 1.2rem;
  }
  
  .priority-notice {
    font-size: 1.8rem;
    padding: 1.5rem;
    border-radius: 12px;
    margin-bottom: 2rem;
  }
}
</style> 